import React from 'react';
import { X, User } from 'lucide-react';
import { useOpenSaleScreen } from "../openSaleScreen/useOpenSaleScreen";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { selectProducts } from "../../../redux/productSlice";
import { sendEmailReceipt } from "../../../redux/paymentTransactionSlice";
import { refundSale } from "../../../redux/salesSlice";
import { toast } from "react-toastify";
import { useQuery } from "../../../utils/useQuery";
import type { RootState } from "../../../redux/store";
import { shopTimeZoneFromShops, toLocalDate } from "../../../utils/timezone";

interface TransactionDetailsModalProps {
    onClose: () => void;
}

const formatMoney = (cents: number) => `$${(cents / 100).toFixed(2)}`;

const TransactionDetailsModal: React.FC<TransactionDetailsModalProps> = ({ onClose }) => {

    const dispatch = useDispatch();
    const query = useQuery();
    const { sale } = useOpenSaleScreen({ onCloseDrawer: () => { } });
    const { products } = useSelector(selectProducts);
    const shops = useSelector((state: RootState) => state.shops) as any[];
    const [confirmingRefund, setConfirmingRefund] = React.useState(false);
    const [refunding, setRefunding] = React.useState(false);

    if (!sale) return null;
    console.log("sale", sale);
    // ------------------------
    // DATE
    // ------------------------
    const appointmentTime = sale.appointmentGroup?.appointments?.[0]?.startTime || sale.createdAt;
    // In the shop's zone, not the viewer's: from India a 10 AM appointment read as 10:30 PM
    const shopTime = appointmentTime ? toLocalDate(appointmentTime, shopTimeZoneFromShops(shops)) : null;
    const formattedDate = shopTime ? dayjs(shopTime).format("MMM D, YYYY") : "-";
    const formattedTime = shopTime ? dayjs(shopTime).format("hh:mm A") : "-";

    const customer = sale.client;

    // ------------------------
    // NORMALIZE ITEMS
    // ------------------------
    const serviceItems = (sale.salesMerchantServices || []).map((s: any) => ({
        id: `service-${s.id}`,
        type: "service",
        name: s?.merchantService?.title,
        practitioner: s?.employee?.name,
        quantity: 1,
        unitPrice: s?.priceCents,
        total: s?.priceCents,
    }));

    const productItems = (sale.products || []).map((p: any) => {
        const matchedProduct = (products || []).find((prod: any) => prod.id === p.productId);
        return {
            id: `product-${p.productId}`,
            type: "product",
            name: matchedProduct?.title || p?.product?.title || p?.title || "Product",
            description: p?.product?.description,
            quantity: p?.quantity || 1,
            unitPrice: p?.priceCents,
            total: p?.totalCents,
        };
    });

    // ------------------------
    // TOTALS (USE BACKEND ONLY)
    // ------------------------
    const subtotal = sale.subtotalCents;
    const tax = sale.taxCents;
    const discount = sale.discountCents;
    const total = sale.totalCents;
    // The sales list API sends the sale's tip as paid_tips_cents
    const tips = sale.tipsCents || sale.paidTipsCents || 0;

    // A pre-auth row only verifies the card - it isn't a payment.
    const transactions = sale.paymentTransactions || [];
    const actualPayments = transactions.filter((t: any) => !(t.preAuth || t.pre_auth));
    const paidVia = actualPayments[0]?.paymentMethod;
    const hasPreAuthOnly = !paidVia && transactions.length > 0;

    // What a refund would give back: every payment still CHARGED.
    const refundable = actualPayments.filter(
        (t: any) => String(t.status ?? "CHARGED").toUpperCase() === "CHARGED" && (t.totalCents || 0) > 0
    );
    const sumCents = (txs: any[]) => txs.reduce((sum: number, t: any) => sum + (t.totalCents || 0), 0);
    const refundCardCents = sumCents(refundable.filter((t: any) => t.paymentMethod === "CARD"));
    const refundCashCents = sumCents(refundable.filter((t: any) => t.paymentMethod === "CASH"));
    const saleStatus = String(sale.status ?? "").toLowerCase();
    const canRefund = refundable.length > 0 && !["refunded", "void"].includes(saleStatus);

    // ------------------------
    // PROCESSING FEE
    // ------------------------
    const processingFee =
        !sale.feeType || !sale.feeBasisPoint
            ? 0
            : (
                sale.feeType === "PERCENTAGE"
                    ? (total * sale.feeBasisPoint) / 10000
                    : sale.feeBasisPoint
            );

    const processingPercentage =
        sale.feeType === "PERCENTAGE"
            ? `(${(sale.feeBasisPoint / 100).toFixed(2)}%)`
            : "";

    // ------------------------
    // ACTION
    // ------------------------
    const handleSendReceipt = async () => {
        const saleId = query.get('saleId');
        try {
            await dispatch(sendEmailReceipt({ saleId }) as any).unwrap();
            toast.success("Receipt sent!");
        } catch (e: any) {
            toast.error(e.message ?? "Something went wrong!");
        }
    };

    const handleRefund = async () => {
        setRefunding(true);
        try {
            await dispatch(refundSale(sale.id) as any).unwrap();
            toast.success(
                refundCashCents > 0
                    ? `Refunded. Hand ${formatMoney(refundCashCents)} cash back to the customer.`
                    : "Refunded to the customer's card."
            );
            setConfirmingRefund(false);
        } catch (e: any) {
            toast.error(typeof e === "string" ? e : "Refund failed. Please try again.");
        } finally {
            setRefunding(false);
        }
    };

    // ------------------------
    // RENDER ITEM
    // ------------------------
    const renderItem = (item: any) => (
        <div key={item.id} className="flex justify-between items-start">
            <div>
                <p className="font-medium text-gray-900">
                    {item.name}
                    {item.quantity > 1 && (
                        <span className="text-gray-500 ml-1">x{item.quantity}</span>
                    )}
                </p>

                <p className="text-xs text-gray-500">
                    {formatMoney(item.unitPrice)} each
                </p>

                {item.type === "service" && item.practitioner && (
                    <p className="text-xs text-gray-400">
                        {item.practitioner}
                    </p>
                )}

                {item.type === "product" && item.description && (
                    <p className="text-xs text-gray-400">
                        {item.description}
                    </p>
                )}
            </div>

            <span className="font-medium text-gray-900">
                {formatMoney(item.total)}
            </span>
        </div>
    );

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <h2 className="text-xl font-semibold text-gray-900">Transaction Details</h2>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="p-6 space-y-6">

                    {/* Sale Info */}
                    <div className="bg-gray-50 rounded-lg p-4">
                        <div className="flex justify-between mb-4">
                            <h3 className="text-lg font-semibold">Sale #{sale.id}</h3>

                            <span className={`px-3 py-1 text-sm rounded-full ${sale.status === 'closed'
                                ? 'bg-green-100 text-green-800'
                                : sale.status === 'open'
                                    ? 'bg-yellow-100 text-yellow-800'
                                    : 'bg-red-100 text-red-800'
                                }`}>
                                {sale.status}
                            </span>
                        </div>

                        <div className="text-sm">
                            {formattedDate} at {formattedTime}
                        </div>
                    </div>

                    {/* Customer */}
                    <div>
                        <h4 className="font-semibold mb-2">Customer</h4>
                        <div className="flex items-center gap-3">
                            <User className="w-5 h-5 text-blue-500" />
                            <div>
                                <p>{customer?.name}</p>
                                <p className="text-sm text-gray-500">{customer?.email}</p>
                            </div>
                        </div>
                    </div>

                    {/* ITEMS */}
                    <div className="space-y-4">

                        {serviceItems.length > 0 && (
                            <>
                                <p className="text-xs uppercase text-gray-500">Services</p>
                                {serviceItems.map(renderItem)}
                            </>
                        )}

                        {productItems.length > 0 && (
                            <>
                                <p className="text-xs uppercase text-gray-500 mt-4">Products</p>
                                {productItems.map(renderItem)}
                            </>
                        )}
                    </div>

                    {/* TOTALS */}
                    <div className="border-t pt-4 space-y-2">

                        <div className="flex justify-between">
                            <span>Subtotal</span>
                            <span>{formatMoney(subtotal)}</span>
                        </div>

                        <div className="flex justify-between">
                            <span>Discount</span>
                            <span>-{formatMoney(discount)}</span>
                        </div>

                        <div className="flex justify-between">
                            <span>Tax</span>
                            <span>{formatMoney(tax)}</span>
                        </div>


                        {tips > 0 && (
                            <div className="flex justify-between">
                                <span>Tip</span>
                                <span>{formatMoney(tips)}</span>
                            </div>
                        )}

                        {/* The sale total excludes the tip (it isn't owed), so add it back for what the customer paid */}
                        <div className="border-t pt-2 flex justify-between font-bold text-lg">
                            <span>Total</span>
                            <span>{formatMoney(total + tips)}</span>
                        </div>

                        <div className="flex justify-between text-sm text-gray-500">
                            <span>Processing Fee {processingPercentage}</span>
                            <span>{formatMoney(processingFee)}</span>
                        </div>
                    </div>

                    {/* Payment */}
                    <div className="text-sm text-gray-500">
                        {paidVia
                            ? `Paid via ${paidVia}`
                            : hasPreAuthOnly
                                ? "Not paid yet - card authorized, charged after the appointment"
                                : "Paid via N/A"}
                    </div>

                    {confirmingRefund && (
                        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm space-y-3">
                            <p className="font-semibold text-red-800">
                                Refund {formatMoney(refundCardCents + refundCashCents)}?
                            </p>
                            {refundCardCents > 0 && (
                                <p>{formatMoney(refundCardCents)} goes back to the customer's card.</p>
                            )}
                            {refundCashCents > 0 && (
                                <p>{formatMoney(refundCashCents)} was paid in cash - hand it back to the customer.</p>
                            )}
                            <p className="text-gray-600">The whole sale is refunded and this can't be undone.</p>
                            <div className="flex justify-end gap-3">
                                <button
                                    disabled={refunding}
                                    onClick={() => setConfirmingRefund(false)}
                                    className="px-4 py-2 border rounded-lg bg-white"
                                >
                                    Cancel
                                </button>
                                <button
                                    disabled={refunding}
                                    onClick={handleRefund}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg disabled:opacity-60"
                                >
                                    {refunding ? "Refunding..." : "Confirm refund"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex justify-end gap-3 pt-4 border-t">
                        <button
                            disabled={!canRefund || confirmingRefund}
                            onClick={() => setConfirmingRefund(true)}
                            className="px-4 py-2 border rounded-lg disabled:bg-gray-200"
                        >
                            {saleStatus === "refunded" ? "Refunded" : "Refund"}
                        </button>

                        <button
                            disabled={sale?.status === 'open'}
                            onClick={handleSendReceipt}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg"
                        >
                            Send Receipt
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default TransactionDetailsModal;